import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Atmosphere } from "../components/Atmosphere";
import { Crown, ChevronRight, Orbit, VenusAndMars } from "lucide-react";

export default function System() {
  // 1. Sprache aus dem Speicher holen & Event-Lauscher für die Navbar aufsetzen
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  // 2. Dynamisch übersetzte Karten-Daten mit Pik (♠) für OW/OB und Herz (♥) für UW/UB
  const cards = [
    {
      type: currentLang === "en" ? "OB" : "OW",
      chinese: "上",
      title: currentLang === "en" ? "Overbound" : "Oberwürfig",
      color: "#C9A227",
      glyph: "♠", // Pik-Symbol für OW/OB
      desc: currentLang === "en"
        ? "Those who lead, protect, and carry responsibility — not out of power, but out of inner duty. Overbounds are the steady hands holding the burdens of others."
        : "Jene, die führen, behüten und Verantwortung tragen — nicht aus Macht, sondern aus innerer Pflicht. Oberwürfige sind die ruhigen Hände, die die Lasten anderer halten.",
      traits: currentLang === "en"
        ? ["Emotionally tolerant", "Intimidating aura", "Resilient", "Socially reclusive"]
        : ["Emotionstolerant", "Einschüchternde Aura", "Belastbar", "Menschenscheu"],
    },
    {
      type: currentLang === "en" ? "UB" : "UW",
      chinese: "下",
      title: currentLang === "en" ? "Underbound" : "Unterwürfig",
      color: "#B01218",
      glyph: "♥", // Herz-Symbol für UW/UB
      desc: currentLang === "en"
        ? "Those who trust, confide, and draw strength from connection. Underbounds are the open heart of Ruìhuá — passionate, artistic, clear."
        : "Jene, die vertrauen, sich anvertrauen und Stärke aus Verbindung schöpfen. UWs sind das offene Herz Ruìhuás — leidenschaftlich, kunstvoll, klar.",
      traits: currentLang === "en"
        ? ["Emotionally sensitive", "Comforting proximity", "Needing tranquility", "Socially close"]
        : ["Emotionssensibel", "Wohltuhende Nähe", "Ruhebedürftig", "Menschennah"],
    },
  ];

  // 3. Die drei Erklärungsboxen übersetzt
  const explanations = [
    {
      icon: Crown,
      title: currentLang === "en" ? "No Hierarchy" : "Keine Hierarchie",
      text: currentLang === "en"
        ? "OB and UB do not represent a hierarchy. An Emperor can be UB, while a servant is OB. It is an inner resonance manifested outwardly and felt through the Elyra, not a social rank."
        : "OW und UW sind keine Hierarchie. Ein Kaiser kann UW sein, ein Diener OW. Es ist eine innere Resonanz, die sich nach außen bemerkbar macht und durch das Elyra spürbar wird, nicht eine soziale Stellung.",
    },
    {
      icon: Orbit,
      title: currentLang === "en" ? "The Relationship Dance" : "Beziehungs-Tanz",
      text: currentLang === "en"
        ? "The most beautiful alliances in Ruìhuá emerge when OB and UB recognize each other — like Yin and Yang."
        : "Die schönsten Bündnisse Ruìhuás entstehen, wenn OW und UW einander erkennen — wie Yin und Yang.",
    },
    {
      icon: VenusAndMars,
      title: currentLang === "en" ? "Childbearing" : "Gebären",
      text: currentLang === "en"
        ? "Without genders in this world, any person can bear a child. A portion of Overbounds is infertile and given privileges to preserve order. 75% can give birth."
        : "Ohne Geschlechter auf der Welt kann jede Person ein Kind kriegen. Ein Teil der Oberwürfigen ist unfruchtbar und wird zur Aufrechterhaltung privilegiert. 75% können gebären.",
    },
  ];

  return (
    <div className="pt-32 pb-24 px-6 relative" data-testid="system-page">
      <Atmosphere variant="minimal" />
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-14"
        >
          <p className="chinese text-[#C9A227]/70 text-2xl mb-3">上 · 下</p>
          <h1 className="serif text-5xl lg:text-7xl text-[#E8DDC8] mb-4">
            {currentLang === "en" ? (
              <>The <span className="italic text-[#C9A227]">OB · UB</span> System</>
            ) : (
              <>Das <span className="italic text-[#C9A227]">OW · UW</span> System</>
            )}
          </h1>
          <p className="serif italic text-[#B3B6B7] text-lg max-w-3xl mx-auto leading-relaxed">
            {currentLang === "en"
              ? "In Ruìhuá, the world is not divided into man and woman — but into holding and being held. A societal and emotional dynamic that affects everyone. Genders do not exist."
              : "In Ruìhuá teilt sich die Welt nicht in Mann und Frau — sondern in Halten und Gehalten-werden. Eine gesellschaftliche und emotionale Dynamik, die jeden betrifft. Geschlechter gibt es nicht."}
          </p>
        </motion.div>

        {/* Playing cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-20" data-testid="cards-grid">
          {cards.map((card, idx) => (
            <motion.div
              key={card.type}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: idx * 0.2 }}
              className="tarot-card p-10 group"
              data-testid={`card-${card.type.toLowerCase()}`}
              style={{ borderColor: `${card.color}88` }}
            >
              <div className="corner-orn tl" style={{ borderColor: card.color }} />
              <div className="corner-orn tr" style={{ borderColor: card.color }} />
              <div className="corner-orn bl" style={{ borderColor: card.color }} />
              <div className="corner-orn br" style={{ borderColor: card.color }} />

              <div className="flex items-start justify-between mb-8">
                <div>
                  <p
                    className="serif text-3xl mb-1"
                    style={{ color: card.color }}
                  >
                    {card.type}
                  </p>
                  <p className="chinese text-5xl" style={{ color: card.color }}>{card.chinese}</p>
                </div>
                <span
                  className="text-6xl serif select-none"
                  style={{ color: `${card.color}80` }}
                >
                  {card.glyph}
                </span>
              </div>

              <h2 className="serif text-4xl text-[#E8DDC8] mb-4">{card.title}</h2>
              <p className="text-[#B3B6B7] leading-relaxed mb-6">{card.desc}</p>

              <ul className="space-y-2 mb-6">
                {card.traits.map((t) => (
                  <li key={t} className="text-sm text-[#E8DDC8] flex items-center gap-2">
                    <span style={{ color: card.color }}>◆</span> {t}
                  </li>
                ))}
              </ul>

              <div className="flex items-end justify-between pt-4 border-t border-[#C9A227]/20">
                <p className="chinese text-2xl rotate-180" style={{ color: `${card.color}60` }}>
                  {card.chinese}
                </p>
                <p className="text-xs tracking-[0.3em] uppercase" style={{ color: card.color }}>
                  {card.type}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Explanation */}
        <div className="grid lg:grid-cols-3 gap-6 mb-20">
          {explanations.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-imperial p-8"
                data-testid={`explain-${i}`}
              >
                <Icon size={26} className="text-[#C9A227] mb-4" />
                <h3 className="serif text-2xl text-[#E8DDC8] mb-3">{b.title}</h3>
                <p className="text-[#B3B6B7] text-sm leading-relaxed">{b.text}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center">
          <p className="serif italic text-[#B3B6B7] text-xl mb-6">
            {currentLang === "en" ? "Which side reflects your heart?" : "Welche Seite spiegelt dein Herz?"}
          </p>
          <Link to="/test" data-testid="goto-quiz">
            <button className="btn-imperial">
              {currentLang === "en" ? "Take the Test" : "Den Test ablegen"} <ChevronRight size={16} />
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}